import styled from '@emotion/styled';
import React, { useEffect, useRef } from 'react';
import { RESIZE_HANDLE_WIDTH } from '@/components/Packaging/constants';

interface Props {
  color: string;
  isRoundLeftBorders: boolean;
  hasAssignedInventory: boolean;
  onResizeStart(): void;
  onResize(delta: { x: number }): void;
  onResizeEnd(): void;
}

export const ResizeHandle: React.FC<Props> = ({ color, isRoundLeftBorders, hasAssignedInventory, onResizeStart, onResize, onResizeEnd }) => {
  const resizeStartPositionRef = useRef<{ x: number } | null>(null);
  const handleResizePointerMoveRef = useRef<((e: PointerEvent) => void) | null>(null);
  const handleResizePointerUpRef = useRef<(() => void) | null>(null);

  const handleResizePointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation(); // prevent parent draggable container from getting control

    if (onResizeStart) {
      onResizeStart();
    }

    resizeStartPositionRef.current = {
      x: e.clientX
    };
  };

  handleResizePointerMoveRef.current = (e: PointerEvent) => {
    if (resizeStartPositionRef.current) {
      onResize({
        x: e.clientX - resizeStartPositionRef.current.x
      });
      resizeStartPositionRef.current = {
        x: e.clientX
      };
    }
  };

  handleResizePointerUpRef.current = () => {
    if (resizeStartPositionRef.current) {
      resizeStartPositionRef.current = null;
      onResizeEnd();
    }
  };

  // TODO: PERFORMANCE. Adding event listener for document.body element inside every overlay item is bad for performance.
  //  There should be single event listener for all overlays. Possibly that would require creating some common React
  //  context (for all overlays) that will provide addMouseUpListener and removeMouseUpListener methods (and same for
  //  mouse move).
  useEffect(() => {
    const moveHandler = (e: PointerEvent) => handleResizePointerMoveRef.current!(e);
    const upHandler = () => handleResizePointerUpRef.current!();
    document.body.addEventListener('pointermove', moveHandler);
    document.body.addEventListener('pointerup', upHandler);

    return () => {
      document.body.removeEventListener('pointermove', moveHandler);
      document.body.removeEventListener('pointerup', upHandler);
    };
  }, []);

  return (
    <Wrapper
      color={color}
      isRoundLeftBorders={isRoundLeftBorders}
      hasAssignedInventory={hasAssignedInventory}
      onPointerDown={handleResizePointerDown}
    >
      <Line hasAssignedInventory={hasAssignedInventory} />
    </Wrapper>
  );
};

const Wrapper = styled.div<{ color: string; isRoundLeftBorders: boolean; hasAssignedInventory: boolean }>`
  cursor: ew-resize;
  position: absolute;
  top: -1px;
  bottom: -1px;
  right: -1px;
  width: ${RESIZE_HANDLE_WIDTH}px;
  background-color: ${({ color, hasAssignedInventory, theme }) => {
    const isDarkTheme = theme.palette.mode === 'dark';
    return hasAssignedInventory ? color : isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
  }};
  border-top-right-radius: ${({ theme }) => theme.shape.borderRadius - 1}px;
  border-bottom-right-radius: ${({ theme }) => theme.shape.borderRadius - 1}px;
  ${({ isRoundLeftBorders, theme }) => {
    if (isRoundLeftBorders) {
      return `
        border-top-left-radius: ${theme.shape.borderRadius - 1}px;
        border-bottom-left-radius: ${theme.shape.borderRadius - 1}px;
      `;
    }
  }}
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Line = styled.div<{ hasAssignedInventory: boolean }>`
  width: 2px;
  height: 40px;
  border-radius: 1px;
  background-color: ${({ hasAssignedInventory, theme }) => {
    const isDarkTheme = theme.palette.mode === 'dark';

    if (isDarkTheme) {
      return hasAssignedInventory ? '#1b1d2799' : 'rgba(255, 255, 255, 0.5)';
    } else {
      return hasAssignedInventory ? '#1b1d2799' : 'rgba(0, 0, 0, 0.5)';
    }
  }};
`;
